import { ComponentBase } from '../../../component-base';
import { IMouseRelease, IMouseDown, IMouseMove, IScaleRelativeToCenter } from '../Interfaces';
import { DrawingsHolderService } from '../drawings-holder.service';
import { PositionCalculatorService } from '../position-calculator.service';
import { ScaleDot } from '../object3D';
import { common } from '../common';

declare let THREE: any;

export abstract class ScaleRelativeToCenterComponent<TObject extends IScaleRelativeToCenter>
  extends ComponentBase
  implements IMouseRelease, IMouseDown, IMouseMove {
  private selectedObject: TObject | undefined;
  private scaleDot: ScaleDot | null = null;
  private raycaster = new THREE.Raycaster(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, -1, 0));
  private lastMousePosition = new THREE.Vector3();
  private dragDirection = new THREE.Vector3();
  private circleHalfSizes = new THREE.Vector3();
  private newSizeOffset = new THREE.Vector3();
  private readonly scaleDotOffset = 7;
  private projectDirection = new THREE.Vector3();

  constructor(
    object3D: new () => TObject,
    private drawings: DrawingsHolderService,
    private positionCalculator: PositionCalculatorService
  ) {
    super();
    this.disable();
    this.drawings.onSelect.subscribe(obj => {
      if (obj) {
        this.selectedObject = obj[0] instanceof object3D ? obj[0] : null;
      } else {
        this.selectedObject = null;
      }
    });
  }

  protected abstract getObjectSize(object: TObject, result): void;

  protected abstract getNewSize(newSize, oldSize): any;

  mouseDown(e: MouseEvent): boolean {
    if (this.selectedObject) {
      const position = this.positionCalculator.calculate(e);
      this.raycaster.ray.origin.copy(position).setY(common.raycastAtY);
      const dots = this.selectedObject.scaler.getDots();
      const intersection = this.raycaster.intersectObjects(dots) as Array<any>;
      if (intersection.length !== 0) {
        this.scaleDot = intersection[0].object as ScaleDot;
        this.getObjectSize(this.selectedObject, this.circleHalfSizes);
        this.projectDirection
          .copy(this.circleHalfSizes)
          .normalize()
          .multiply(this.scaleDot.direction);
        this.lastMousePosition.copy(position);
        return true;
      }
    }
    return false;
  }

  mouseMove(e: MouseEvent): boolean {
    if (this.scaleDot) {
      const currentMousePosition = this.positionCalculator.calculate(e);

      const newSize = this.calculateNewSize(currentMousePosition);

      if (newSize) {
        const { x: newWidth, z: newHeight } = newSize;
        this.circleHalfSizes.set(newWidth, 0, newHeight);
        this.selectedObject.update(newWidth, newHeight);
      }

      this.lastMousePosition.copy(currentMousePosition);
      return true;
    }
    return false;
  }

  private calculateNewSize(currentMousePosition): any {
    const directions = this.scaleDot.directions;
    const results = [];

    for (let i = 0; i < directions.length; i++) {
      const direction = directions[i];
      const dragDirection = this.dragDirection
        .copy(currentMousePosition)
        .sub(this.lastMousePosition)
        .projectOnVector(direction)
        .normalize();

      const dragLength =
        currentMousePosition
          .clone()
          .sub(this.selectedObject.position)
          .projectOnVector(this.projectDirection)
          .projectOnVector(dragDirection)
          .length() - this.scaleDotOffset;

      if (isNaN(dragLength)) {
        break;
      }

      const newSizeOffset = this.newSizeOffset
        .set(dragLength, 0, dragLength)
        .multiply(dragDirection);

      const newSize = this.getNewSize(newSizeOffset, this.circleHalfSizes);
      results.push(newSize);
    }

    if (results.length !== directions.length) {
      return null;
    }

    return results.length === 1 ? results[0] : new THREE.Vector3(results[0].x, 0, results[1].z);
  }

  mouseUp(): boolean {
    if (this.scaleDot) {
      this.scaleDot = null;
      return true;
    }
    return false;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/proTools/components/ScaleRelativeToCenterComponent.ts